import React from 'react';
import { observable, action, makeObservable } from 'mobx';
import { observer } from 'mobx-react';
import { CirclePicker } from 'react-color';

import { type CategoryInfo } from '@root/models/CategoryInfo';
import { PromiseCompletion } from '@classes/PromiseCompletion';
import { apiService } from '@services/ApiService';
import { notificationService } from '@services/NotificationService';
import { type IModalDialogContent, type ModalButtonOptions, ModalButtonType, type ModalDialogOptions, ModalWindow } from '@components/Modal';

export type AddCategoryDialogProps = {
  onSaved: () => void;
};

@observer
export default class AddCategoryDialog extends React.Component<AddCategoryDialogProps> implements IModalDialogContent<void> {
  @observable private _name: string = '';
  @observable private _color: string = '#ffffff';
  private _loader: PromiseCompletion = new PromiseCompletion();
  private _inputRef: React.RefObject<HTMLInputElement> = React.createRef();

  constructor(props: AddCategoryDialogProps){
    super(props);
    makeObservable(this);
  }

  componentDidMount(){
    this._inputRef.current?.select();
  }

  public getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
    const buttons: ModalButtonOptions<void>[] = [];

    buttons.push({
      type: ModalButtonType.Cancel,
      onClick: () => {
        window.close();
      }
    });
    buttons.push({
      title: 'Add',
      type: ModalButtonType.Add,
      onClick: async () => {
        await this._onSaveClick(window);
      }
    });

    return {
      title: `New category`,
      width: 600,
      buttons: buttons,
      loader: this._loader
    };
  }

  render(){
    return (
      <>
        <div className="form-group">
          <label className="form-control-label">Name</label>
          <input className="form-control" ref={this._inputRef} autoFocus type="text" value={this._name} onChange={(e) => this._name = e.target.value} />
        </div>

        <div className="form-group">
          <label className="form-control-label">Color</label>
          <div className="form-color-picker">
            <span className="form-color-swatch" style={{ background: this._color }} />
            <CirclePicker
              color={this._color}
              onChange={(e) => action(() => { this._color = e.hex; })()}
            />
          </div>
        </div>
      </>
    );
  }

  @action.bound
  private async _onSaveClick(window: ModalWindow<void>){
    const name = this._name.trim();
    if (!name){
      notificationService.error('Category name cannot be empty.');
      return;
    }

    const created = await apiService.post<CategoryInfo>('/api/note/category', {
      name: name,
      color: this._color
    }, {
      loader: this._loader
    });

    this.props.onSaved();
    notificationService.success(`Category "${created.name ?? name}" was added!`);
    window.close(ModalButtonType.Add);
  }
}
